<template>
  <layout>
    <div class="page-header">
      <div class="page-title">
        <h2>{{$t('moods')}}</h2>
        <h5>{{$t('moods_description')}}</h5>
      </div>
    </div>

    <draggable
        v-model="moods"
        class="mood-cards-grid"
        :animation="200"
        item-key="id"
        handle=".drag-handle"
        @end="onDragEnd"
        :disabled="isEditMode"
    >
      <template #item="{element, index}">
        <div class="mood-card" :class="{ 'edit-mode': editingIndex === index }">
          <span class="drag-handle" v-if="!isEditMode">⋮⋮</span>

          <span
              class="edit-button"
              @click="startEdit(index)"
              v-if="!isEditMode"
          >
            <img src="@/assets/images/editPen.svg" />
          </span>

          <template v-if="editingIndex !== index">
            <img class="icon-holder" :src="getIconPath(element.icon)"/>
            <span class="mood-title">{{element.title}}</span>
            <span class="order-badge">{{element.order}}</span>
          </template>

          <template v-else>
            <div class="edit-form">
              <IconPicker
                  v-model="editForm.icon"
                  class="edit-icon-picker"
              />

              <input
                  v-model="editForm.title"
                  type="text"
                  class="edit-title-input"
                  :placeholder="$t('enter_title')"
                  @keyup.enter="saveEdit"
                  @keyup.esc="cancelEdit"
              />

              <div class="edit-actions">
                <button class="save-btn" @click="saveEdit">
                  {{ $t("save") }}
                </button>
                <button class="cancel-btn" @click="cancelEdit">
                  {{ $t("cancel_button") }}
                </button>
              </div>
            </div>
          </template>
        </div>
      </template>
    </draggable>

    <div
        class="change-order-button"
        :class="{ 'active': orderChanged }"
        @click="applyOrderChanges"
    >
      <span>{{$t("apply_order")}}</span>
    </div>

    <div class="sliders-section">
      <div class="section-header">
        <div class="section-title">
          <h2>{{$t('sliders')}}</h2>
        </div>
        <div class="section-actions">
          <div class="log-icon-wrapper">
            <img src="@/assets/images/logIconNew.svg" @click="showLogModal" class="log-icon" />
            <span class="tooltip">{{ $t('log_records') }}</span>
          </div>
          <LogModal
              :visible="isLogModalVisible"
              :on-hide="hideLogModal"
              :data="logData"
              :total-items="logMeta.total"
              :current-page="logMeta.currentPage"
              :per-page="logMeta.perPage"
              :on-page-change="handleLogPageChange"
          />
          <router-link to="/slider/new">
            <div class="justify-end-add-btn no-text-select">
              <img src="../assets/images/plus.svg" />
              <span>{{$t('add_new')}}</span>
            </div>
          </router-link>
        </div>
      </div>

      <div class="slider-list-grid">
        <SliderCard
            v-for="item in sliderData.data"
            :key="item.sliderId"
            :image="item.imageUrl"
            :title="item.name"
            :to="`/slider/${item.sliderId}`"
        />

        <SliderCard v-if="isSliderLoading" :is-loading="true" />
        <SliderCard v-if="isSliderLoading" :is-loading="true" />
        <SliderCard v-if="isSliderLoading" :is-loading="true" />
      </div>

      <div class="pagination-align-center" v-if="sliderData.total > 20">
        <Pagination :current-page="sliderCurrentPage" :total-items="sliderData.total" :per-page="20" :on-page-change="handleSliderPage"/>
      </div>
    </div>

  </layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import IconPicker from "@/components/IconPicker.vue";
import moodService from "@/service/moodService";
import sliderService from "@/service/sliderService";
import TestFilters from "@/components/tables/Test/TestFilters.vue";
import ExpandableSearch from "@/components/ExpandableSearch.vue";
import SliderCard from "@/components/SliderCard.vue";
import Pagination from "@/components/Pagination.vue";
import LogModal from "@/components/modals/LogModal.vue";
import draggable from 'vuedraggable';
import { getIconPath } from '@/assets/icons.js';
import { handleErrorResponse } from "@/utils/utils";

export default {
  components: {
    ExpandableSearch,
    TestFilters,
    Layout,
    IconPicker,
    draggable,
    SliderCard,
    Pagination,
    LogModal
  },
  data(){
    return {
      // Moods data
      moods: [],
      orderChanged: false,
      originalOrder: [],
      editingIndex: -1,
      editForm: {
        icon: '',
        title: ''
      },
      // Sliders data
      sliderData: { data: [], total: 0 },
      isSliderLoading: true,
      sliderCurrentPage: 1,
      logData: [],
      logMeta: {
        total: 0,
        currentPage: 1,
        perPage: 20
      },
      isLogModalVisible: false
    }
  },
  computed: {
    isEditMode() {
      return this.editingIndex !== -1;
    }
  },
  created() {
    this.getMoods();
    this.getSliders(1);
  },
  methods: {
    getMoods() {
      moodService.getMoods().then(res => {
        this.moods = res.data.data
            .map((mood, index) => ({
              ...mood,
              id: mood.id || mood.uuid || `mood-${index}`
            }))
            .sort((a, b) => a.order - b.order);

        this.originalOrder = this.moods.map(mood => ({
          id: mood.id,
          order: mood.order
        }));

      })
    },
    // Merkezi fonksiyonu kullan
    getIconPath,

    onDragEnd(event) {
      this.moods.forEach((mood, index) => {
        mood.order = index + 1;
      });

      this.checkOrderChanged();
    },

    checkOrderChanged() {
      this.orderChanged = !this.moods.every((mood, index) => {
        const originalMood = this.originalOrder.find(om => om.id === mood.id);
        return originalMood && originalMood.order === mood.order;
      });
    },

    applyOrderChanges() {
      if (!this.orderChanged) return;

      const updatedOrders = {
        orders: this.moods.map(mood => ({
          uuid: mood.uuid || mood.id,
          order: mood.order
        }))
      };

      moodService.updateOrder(updatedOrders).then(res => {
        this.originalOrder = this.moods.map(mood => ({
          id: mood.id,
          order: mood.order
        }));

        this.orderChanged = false;

        this.$snackbar.add({
          title: this.$t("order_changed_successfully"),
          type: 'success'
        });
      }).catch(err => {
        this.getMoods();

        this.$snackbar.add({
          title: this.$t("order_change_failed"),
          type: 'error'
        });
      });
    },

    startEdit(index) {
      this.editingIndex = index;
      const mood = this.moods[index];
      this.editForm = {
        icon: mood.icon,
        title: mood.title
      };
    },

    saveEdit() {
      if (this.editingIndex === -1) return;

      const mood = this.moods[this.editingIndex];

      const updateData = {
        id: mood.uuid || mood.id,
        title: this.editForm.title,
        icon: this.editForm.icon, // Artık icon ID'si gönderiyoruz
        order: mood.order
      };

      moodService.updateMood(updateData).then(res => {
        mood.icon = this.editForm.icon;
        mood.title = this.editForm.title;

        this.editingIndex = -1;
        this.editForm = { icon: '', title: '' };

        this.$snackbar.add({
          title: this.$t("mood_updated"),
          type: 'success'
        });
      }).catch(err => {
        this.$snackbar.add({
          title: this.$t("mood_update_failed"),
          type: 'error'
        });

        this.cancelEdit();
      });
    },

    cancelEdit() {
      this.editingIndex = -1;
      this.editForm = { icon: '', title: '' };
    },

    // Slider methods
    getSliders(page) {
      this.isSliderLoading = true;
      sliderService.getSliders(page).then((response) => {
        this.sliderData = response.data.data;
      }).catch((error) => {
        handleErrorResponse(error, this.$snackbar);
      }).finally(() => {
        this.isSliderLoading = false;
      });
    },

    handleSliderPage(page) {
      this.sliderCurrentPage = page;
      this.getSliders(page);
    },

    getLogs(page = 1) {
      sliderService.getLogs(this.$i18n.locale, page, 20).then((res) => {
        const response = res.data.data;
        this.logData = response.data;
        this.logMeta = {
          total: response.total,
          currentPage: response.current_page,
          perPage: response.per_page
        };
      }).catch((err) => {
        handleErrorResponse(err, this.$snackbar);
      });
    },

    handleLogPageChange(page) {
      if (this.logMeta.currentPage === page) return;
      this.logMeta.currentPage = page;
      this.getLogs(page);
    },

    showLogModal() {
      this.isLogModalVisible = true;
      this.logMeta.currentPage = 1;
      this.getLogs(1);
    },

    hideLogModal() {
      this.isLogModalVisible = false;
    }
  }
}
</script>

<style scoped>
.mood-cards-grid {
  width: 100%;
  display: inline-grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
}

.mood-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 5px 15px;
  min-height: 200px;
  width: 100%;
  border: 1px solid #EAECF0;
  height: 100%;
  border-radius: 20px;
  cursor: default;
  position: relative;
  transition: all 0.3s ease;
  user-select: none;
}

.mood-card:hover {
  box-shadow: 0 4px 20px rgba(16, 24, 40, 0.1);
  transform: translateY(-2px);
}

.mood-card.sortable-ghost {
  opacity: 0.4;
}

.mood-card.sortable-drag {
  opacity: 1;
  box-shadow: 0 8px 30px rgba(16, 24, 40, 0.15);
}

.mood-card.edit-mode {
  border-color: #4A68F8;
  box-shadow: 0 0 0 3px rgba(74, 104, 248, 0.1);
}

.drag-handle {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #98A2B3;
  font-size: 16px;
  cursor: grab;
  padding: 5px;
  user-select: none;
  z-index: 10;
}

.drag-handle:hover {
  color: #667085;
}

.drag-handle:active {
  cursor: grabbing;
  color: #344054;
}

.edit-button {
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 16px;
  cursor: pointer;
  padding: 5px;
  user-select: none;
  z-index: 10;
  transition: transform 0.2s ease;
}

.edit-button:hover {
  transform: scale(1.1);
}

.icon-holder {
  height: 150px;
  width: 150px;
  object-fit: contain;
  margin-bottom: 10px;
  pointer-events: none;
}

.mood-title {
  font-size: 16px;
  font-weight: 500;
  color: #344054;
  text-align: center;
  max-width: 90%;
  word-wrap: break-word;
}

.order-badge {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: #F2F4F7;
  color: #667085;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  pointer-events: none;
}

/* Edit form styles */
.edit-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 20px 10px;
}

.edit-icon-picker {
  margin-top: 10px;
}

.edit-title-input {
  width: 90%;
  padding: 10px 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
  text-align: center;
}

.edit-title-input:focus {
  border-color: #4A68F8;
  box-shadow: 0 0 0 3px rgba(74, 104, 248, 0.1);
}

.edit-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.save-btn, .cancel-btn {
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.save-btn {
  background: #4A68F8;
  color: white;
  border-color: #4A68F8;
}

.save-btn:hover {
  background: #3651E0;
  border-color: #3651E0;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(74, 104, 248, 0.2);
}

.cancel-btn {
  background: white;
  color: #667085;
  border-color: #E4E7EC;
}

.cancel-btn:hover {
  background: #F9FAFB;
  border-color: #D0D5DD;
}

.change-order-button {
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-end;
  border-radius: 16px;
  height: 36px;
  padding: 0 12px;
  background-color: #E4E7EC;
  color: #98A2B3;
  cursor: not-allowed;
  transition: all 0.2s ease-in-out;
  border: 1px solid #E4E7EC;
  text-wrap: nowrap;
  width: fit-content;
  min-width: 120px;
  font-weight: 500;
  opacity: 0.6;
}

.change-order-button.active {
  background-color: #4A68F8;
  color: #FFFFFF;
  cursor: pointer;
  border: 1px solid #4A68F8;
  opacity: 1;
}

.change-order-button.active:hover {
  background-color: #3651E0;
  border-color: #3651E0;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(74, 104, 248, 0.3);
}

.change-order-button.active:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(74, 104, 248, 0.2);
}

@media (max-width: 975px) {
  .mood-cards-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}

/* Sliders Section Styles */
.sliders-section {
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #EAECF0;
}

.section-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.section-title h2 {
  font-family: euclid_medium, sans-serif;
  font-size: 20px;
  color: #040C2D;
  margin: 0;
}

.section-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.slider-list-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.log-icon-wrapper {
  position: relative;
  cursor: pointer;
}

.log-icon-wrapper .tooltip {
  visibility: hidden;
  background-color: #040C2D;
  color: white;
  text-align: center;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-family: euclid_semi_bold, sans-serif;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  white-space: nowrap;
}

.log-icon-wrapper:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.log-icon-wrapper .tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #040C2D transparent transparent transparent;
}

.pagination-align-center {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

@media (max-width: 1024px) {
  .slider-list-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .slider-list-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}
</style>
